
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Campaign } from "@/hooks/useCampaigns";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Copy as CopyIcon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  MoreHorizontal as DotsHorizontalIcon,
  Pencil as PencilIcon,
  PlayCircle,
  Trash2Icon
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { objectsToCSV, downloadCSV } from "@/utils/csvUtils";

interface CampaignActionsProps {
  campaign: Campaign;
  onPublish: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
}

export function CampaignActions({ 
  campaign, 
  onPublish, 
  onDelete, 
  onDuplicate 
}: CampaignActionsProps) {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  
  const handleViewCampaign = (id: string) => {
    navigate(`/admin/campaigns/${id}`);
  };
  
  const handleEditCampaign = (id: string) => {
    navigate(`/admin/campaigns/edit/${id}`);
  };

  const handleExportVouchers = async (campaignId: string) => {
    try {
      setIsExporting(true);
      toast.info("Preparing vouchers export...");
      
      // Fetch vouchers for this campaign
      const { data: vouchers, error } = await supabase
        .from('vouchers')
        .select(`
          id, 
          code, 
          status, 
          created_at, 
          claimed_by, 
          claimed_at, 
          redeemed_at, 
          redeemed_location(name)
        `)
        .eq('campaign_id', campaignId);
      
      if (error) throw error;
      
      if (!vouchers || vouchers.length === 0) {
        toast.warning("No vouchers found for this campaign");
        return;
      }
      
      // Format data for CSV
      const csvData = vouchers.map(voucher => ({
        code: voucher.code,
        status: voucher.status,
        created_at: voucher.created_at ? new Date(voucher.created_at).toLocaleString() : '',
        claimed_by: voucher.claimed_by || '',
        claimed_at: voucher.claimed_at ? new Date(voucher.claimed_at).toLocaleString() : '',
        redeemed_at: voucher.redeemed_at ? new Date(voucher.redeemed_at).toLocaleString() : '',
        redeemed_location: voucher.redeemed_location?.name || ''
      }));
      
      // Generate CSV and trigger download
      const csv = objectsToCSV(csvData);
      const fileName = `${campaign.title.replace(/\s+/g, '_')}_vouchers_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csv, fileName);
      
      toast.success("Vouchers exported successfully");
    } catch (error) {
      console.error("Error exporting vouchers:", error);
      toast.error("Failed to export vouchers");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isExporting}>
          <DotsHorizontalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleViewCampaign(campaign.id)}>
          <EyeIcon className="mr-2 h-4 w-4" /> View
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEditCampaign(campaign.id)}>
          <PencilIcon className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDuplicate(campaign)}>
          <CopyIcon className="mr-2 h-4 w-4" /> Duplicate
        </DropdownMenuItem>
        
        {campaign.status === 'draft' && (
          <DropdownMenuItem onClick={() => onPublish(campaign)}>
            <PlayCircle className="mr-2 h-4 w-4" /> Publish
          </DropdownMenuItem>
        )}
        
        {campaign.status !== 'draft' && (
          <DropdownMenuItem 
            onClick={() => handleExportVouchers(campaign.id)}
            disabled={isExporting}
          >
            <DownloadIcon className="mr-2 h-4 w-4" /> Export Vouchers
          </DropdownMenuItem>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => onDelete(campaign)}
        >
          <Trash2Icon className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
